import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getKpiEligibleUsers } from '@/api/kpi'
import { pushNotification, useAppDispatch } from '@/store'
import { EligibleUserModel } from '@/types'

interface UseKpiEligibleUsersReturn {
  eligibleUsers: EligibleUserModel[] | null
  loading: boolean
  fetchEligibleUsers: () => Promise<void>
}

const useKpiEligibleUsers = (): UseKpiEligibleUsersReturn => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [eligibleUsers, setEligibleUsers] = useState<
    EligibleUserModel[] | null
  >(null)
  const [loading, setLoading] = useState<boolean>(false)

  const fetchEligibleUsers = useCallback(async () => {
    setLoading(true)

    try {
      const data = await getKpiEligibleUsers()

      const transformedUsers: EligibleUserModel[] = data.eligible_users.map(
        (user) => ({
          id: user.id,
          username: user.username,
          email: user.email,
          fullName: user.full_name,
        })
      )

      setEligibleUsers(transformedUsers)
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to fetch eligible users')

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    eligibleUsers,
    loading,
    fetchEligibleUsers,
  }
}

export default useKpiEligibleUsers
